import { MessageRole } from "models/enumerations/conversations/message-role";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import { upsertMessageParts } from "models/view-models/conversations/message-part-factory";
import { CollectionUtils } from "utilities/collection-utils";
import StringUtils from "utilities/string-utils";

export interface ConversationMessageStore {
    /**
     * Builds merged messages from the stored messages.
     * It creates a base message array with a system message at index 0
     * and merges messages with the same index.
     * @param status - Optional status to set for the messages.
     * @returns An array of merged conversation messages.
     */
    build(status?: ConversationMessageStatus): Array<ConversationMessageRecord>;

    /**
     * Adds new messages to the store.
     * @param addedMessages - The messages to add.
     * @returns The conversation message store instance for chaining.
     */
    add(
        ...addedMessages: (ConversationMessageRecord | undefined)[]
    ): ConversationMessageStore;

    /**
     * Remove all messages from the store that have the provided message correlation id
     */
    removeById(id?: string): ConversationMessageStore;
}

/**
 * Creates a conversation message store. This contains all of the messages returned from the API.
 * The chat UI components handle which messages to display and get the citations for each message.
 * @returns A conversation message store.
 */
export function createConversationMessageStore(
    startingMessages: ConversationMessageRecord[] = []
) {
    const messages: Array<ConversationMessageRecord> = startingMessages
        .filter((m) => m != null)
        .reduce(mergeIncomingMessages, []);

    const self: ConversationMessageStore = {
        build(status) {
            const currentMessages = buildCurrentMessages(messages);
            return currentMessages.map(withStatus(status));
        },

        add(...addedMessages) {
            return createConversationMessageStore([
                ...messages,
                ...addedMessages.filter((m) => m != null),
            ]);
        },

        removeById(id) {
            if (!id) return self;

            const filteredMessages = messages.filter((m) => m.id !== id);

            if (filteredMessages.length === messages.length) {
                return self;
            }

            return createConversationMessageStore(filteredMessages);
        },
    };

    return self;
}

/**
 * Get the latest message based on createdOn timestamp.
 */
function getLatestMessage(
    latest: ConversationMessageRecord | undefined,
    msg: ConversationMessageRecord
) {
    if (!latest || !latest.createdOn) return msg;
    if (!msg.createdOn) return latest;

    if (msg.createdOn === latest.createdOn) {
        if (!latest.index) return msg;
        if (!msg.index) return latest;
        return msg.index > latest.index ? msg : latest;
    }

    return msg.createdOn > latest.createdOn ? msg : latest;
}

/**
 * Builds the current message chain from the latest message by following parentId links.
 * This ensures that the most recent message is included and excludes any messages that the user has regenerated.
 */
function buildCurrentMessages(messages: Array<ConversationMessageRecord>) {
    const latest = messages.reduce(getLatestMessage, messages.at(0));
    const result: ConversationMessageRecord[] = [];
    let current: ConversationMessageRecord | undefined = latest;

    while (current) {
        result.unshift(current);

        const parentId = current.parentId;
        current = parentId
            ? messages.find((m) => m.id === parentId)
            : undefined;
    }

    return result;
}

/**
 * Merges incoming messages into the existing messages array.
 * If a message with the same index and id (if available) exists, it merges their content and citations.
 * Otherwise, it adds the incoming message to the array.
 */
function mergeIncomingMessages(
    acc: Array<ConversationMessageRecord>,
    msg: ConversationMessageRecord
): Array<ConversationMessageRecord> {
    const index = acc.findIndex((m) => m.id === msg.id);

    if (index === -1) {
        return [...acc, msg];
    }

    return CollectionUtils.replaceElementAt(
        acc,
        index,
        mergeMessageWithFoundMessage(msg, acc[index])
    );
}

function mergeMessageWithFoundMessage(
    incomingMessage: ConversationMessageRecord,
    foundMessage?: ConversationMessageRecord
): ConversationMessageRecord {
    if (!foundMessage) {
        return incomingMessage;
    }

    return foundMessage.with({
        createdOn: incomingMessage.createdOn ?? foundMessage.createdOn,
        id: incomingMessage.id ?? foundMessage.id,
        conversationId:
            incomingMessage.conversationId ?? foundMessage.conversationId,
        role: incomingMessage.role ?? foundMessage.role,
        content: mergeContent(foundMessage, incomingMessage),
        parentId: incomingMessage.parentId ?? foundMessage.parentId,
        chatFinishReason:
            incomingMessage.chatFinishReason ?? foundMessage.chatFinishReason,
        citations: [
            ...foundMessage.citations,
            ...incomingMessage.citations,
        ].filter((citation) => StringUtils.hasValue(citation?.title)),
        parts: upsertMessageParts(foundMessage.parts, incomingMessage.parts),
        status: incomingMessage.status ?? foundMessage.status,
        index: incomingMessage.index ?? foundMessage.index,
        feedback: incomingMessage.feedback ?? foundMessage.feedback,
        toolCalls: incomingMessage.toolCalls ?? foundMessage.toolCalls,
    });
}

function mergeContent(
    foundMessage: ConversationMessageRecord,
    incomingMessage: ConversationMessageRecord
): string | undefined {
    const existingContent = foundMessage?.content;
    const newContent = incomingMessage?.content;

    if (!newContent) {
        return existingContent;
    }

    if (!existingContent || foundMessage.role === MessageRole.User) {
        return newContent;
    }

    return `${existingContent}${newContent}`;
}

/**
 * Get the status of a message based on its role and content.
 * @param message - The message to check.
 * @returns The status of the message.
 */
function getStatus(
    message: ConversationMessageRecord,
    status?: ConversationMessageStatus
): ConversationMessageStatus {
    if (message.role === MessageRole.User) {
        return ConversationMessageStatus.Completed;
    }

    if (status) {
        return status;
    }

    if (message.content) {
        return ConversationMessageStatus.Streaming;
    }

    return ConversationMessageStatus.Thinking;
}

function withStatus(status?: ConversationMessageStatus) {
    return (msg: ConversationMessageRecord) =>
        msg.with({ status: getStatus(msg, status) });
}
